/**
 * 発注キャンセルモーダル
 *
 * 設計書 05-frontend-design.md §5.6 整合。
 * 理由コードを Radio で選択し、`OTHER` 選択時は詳細記述を必須とする。
 * Popconfirm で二重確認後に `cancelPurchaseOrder` API を呼ぶ。
 */
import { useState } from 'react';
import { Button, Modal, Form, Input, Radio, Descriptions, Popconfirm, message } from 'antd';
import { logger } from '@/shared/lib/logger';
import { cancelPurchaseOrder } from '../services/purchaseOrderApiService';
import { USER_SELECTABLE_CANCEL_REASONS } from '../types/purchaseOrder';
import type {
  CancelPurchaseOrderRequest,
  PurchaseOrderViewModel,
  UserSelectableCancelReasonCode,
} from '../types/purchaseOrder';

const { TextArea } = Input;

const CANCEL_REASON_DETAIL_TEXTAREA_ROWS = 3;

/** Radio に表示するキャンセル理由ラベル */
const USER_SELECTABLE_CANCEL_REASON_LABEL: Record<UserSelectableCancelReasonCode, string> = {
  MISTAKE: '誤発注',
  DUPLICATE: '重複発注',
  DELAY: '納期遅延',
  OTHER: 'その他',
};

interface FormValues {
  readonly cancelReasonCode: UserSelectableCancelReasonCode;
  readonly cancelReasonDetail?: string;
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCancelled: (params: { readonly cancelled: PurchaseOrderViewModel }) => void;
  readonly purchaseOrder: PurchaseOrderViewModel;
}

export function PurchaseOrderCancelModal({
  open,
  onClose,
  onCancelled,
  purchaseOrder,
}: Props): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // selectedReason は Form 値の単一情報源化のため Form.useWatch で参照
  const selectedReason = Form.useWatch('cancelReasonCode', form);

  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const request: CancelPurchaseOrderRequest = {
        cancelReasonCode: values.cancelReasonCode,
        cancelReasonDetail: values.cancelReasonDetail ?? null,
      };
      const cancelled = await cancelPurchaseOrder({
        purchaseOrderId: purchaseOrder.id,
        request,
      });
      message.success('発注をキャンセルしました');
      form.resetFields();
      onCancelled({ cancelled });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'キャンセルに失敗しました';
      message.error(errorMessage);
      logger.captureError(error, `PurchaseOrderCancelModal.handleSubmit(${purchaseOrder.id})`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    form.resetFields();
    onClose();
  };

  const isOtherSelected = selectedReason === 'OTHER';

  return (
    <Modal
      open={open}
      title="発注キャンセル"
      footer={[
        <Button key="close" onClick={handleClose}>
          閉じる
        </Button>,
        <Popconfirm
          key="confirm"
          title="本当にキャンセルしますか?"
          description="この操作は取り消せません。"
          okText="キャンセルする"
          cancelText="やめる"
          onConfirm={() => form.submit()}
        >
          <Button type="primary" danger loading={isSubmitting}>
            キャンセル実行
          </Button>
        </Popconfirm>,
      ]}
      onCancel={handleClose}
      destroyOnHidden
    >
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="現場">{purchaseOrder.siteName}</Descriptions.Item>
        <Descriptions.Item label="材料">{purchaseOrder.materialTypeName}</Descriptions.Item>
        <Descriptions.Item label="発注数量">{purchaseOrder.orderedQuantity}</Descriptions.Item>
        <Descriptions.Item label="発注者">{purchaseOrder.orderedByUserName}</Descriptions.Item>
      </Descriptions>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="cancelReasonCode"
          label="キャンセル理由"
          rules={[{ required: true, message: 'キャンセル理由を選択してください' }]}
        >
          <Radio.Group>
            {USER_SELECTABLE_CANCEL_REASONS.map((reason) => (
              <Radio key={reason} value={reason}>
                {USER_SELECTABLE_CANCEL_REASON_LABEL[reason]}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {isOtherSelected && (
          <Form.Item
            name="cancelReasonDetail"
            label="詳細記述(必須)"
            rules={[{ required: true, message: '詳細を入力してください' }]}
          >
            <TextArea
              rows={CANCEL_REASON_DETAIL_TEXTAREA_ROWS}
              placeholder="その他の理由を具体的に記述"
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
